// services/superAdminAPI.js
import axios from 'axios';

import { API_BASE_URL } from '../config';

class SuperAdminAPI {
    constructor() {
        // Base URL for API requests
        this.baseURL = API_BASE_URL;

        // Create axios instance with default configs
        this.api = axios.create({
            baseURL: this.baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor to automatically add auth token
        this.api.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('accessToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Add response interceptor for handling token refresh
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // If the error is 401 (Unauthorized) and we haven't tried to refresh the token yet
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        // Try to refresh the token
                        const refreshToken = localStorage.getItem('refreshToken');
                        if (!refreshToken) {
                            // No refresh token available, redirect to login
                            this.logout();
                            return Promise.reject(new Error('Session expired. Please login again.'));
                        }

                        const response = await axios.post(`${this.baseURL}/token/refresh`, {
                            refresh_token: refreshToken
                        });

                        if (response.data.access_token) {
                            localStorage.setItem('accessToken', response.data.access_token);
                            // Keep the refresh token if it's returned, otherwise keep the current one
                            if (response.data.refresh_token) {
                                localStorage.setItem('refreshToken', response.data.refresh_token);
                            }

                            // Update the header and retry the original request
                            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
                            return this.api(originalRequest);
                        }
                    } catch (refreshError) {
                        // If refresh fails, logout and redirect to login
                        console.error('Token refresh failed:', refreshError);
                        this.logout();
                        return Promise.reject(new Error('Session expired. Please login again.'));
                    }
                }

                // Handle other errors
                return Promise.reject(error);
            }
        );
    }

    // Helper to get auth headers for requests
    getAuthHeaders() {
        const token = localStorage.getItem('accessToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!localStorage.getItem('accessToken');
    }

    // Login function - updated to match new backend auth
    async login(email, password) {
        try {
            // Using the OAuth2 password flow expected by FastAPI
            const formData = new URLSearchParams();
            formData.append('username', email);
            formData.append('password', password);

            const response = await axios.post(
                `${this.baseURL}/superadmin/login`,
                formData,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            );

            const { access_token, refresh_token } = response.data;

            if (access_token) {
                localStorage.setItem('accessToken', access_token);
                if (refresh_token) {
                    localStorage.setItem('refreshToken', refresh_token);
                }
                return true;
            } else {
                throw new Error('Login failed. No token received.');
            }
        } catch (error) {
            console.error('Login error:', error);
            throw new Error(
                error.response?.data?.detail ||
                error.message ||
                'Invalid credentials or server error'
            );
        }
    }

    // Logout function
    logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
    }

    // Test connection to API
    async testConnection() {
        try {
            // If we have a token, use it
            const headers = this.getAuthHeaders();
            const response = await this.api.get('/superadmin/system/health', { headers });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Connection test failed:', error);

            // If it's an auth error, we don't consider it a connection failure
            // It means the backend is responding, just not allowing access
            if (error.response?.status === 401) {
                return {
                    success: true,
                    authenticated: false,
                    message: 'Backend is reachable but authentication is required'
                };
            }

            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Connection failed'
            };
        }
    }

    // Get dashboard stats
    async getDashboardStats() {
        try {
            const response = await this.api.get('/superadmin/dashboard/stats', {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    }

    // Get system health
    async getSystemHealth() {
        try {
            const response = await this.api.get('/superadmin/system/health', {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching system health:', error);
            throw error;
        }
    }

    // Get schools list
    async getSchools(params = {}) {
        try {
            const response = await this.api.get('/superadmin/schools', {
                headers: this.getAuthHeaders(),
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching schools:', error);
            throw error;
        }
    }

    // Get a single school
    async getSchool(schoolId) {
        try {
            const response = await this.api.get(`/superadmin/schools/${schoolId}`, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error(`Error fetching school ${schoolId}:`, error);
            throw error;
        }
    }

    // Create a new school - Updated to match backend requirements
    async createSchool(schoolData) {
        try {
            // Ensure all required fields are present for admin creation
            if (schoolData.admin_name && schoolData.admin_email) {
                console.log('Creating school with admin:', {
                    name: schoolData.admin_name,
                    email: schoolData.admin_email
                });
            } else {
                console.warn('Creating school without admin details. Admin will need to be created separately.');
            }

            const response = await this.api.post('/superadmin/schools', schoolData, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Error creating school:', error);
            throw error;
        }
    }

    // Update a school
    async updateSchool(schoolId, updateData) {
        try {
            const response = await this.api.put(`/superadmin/schools/${schoolId}`, updateData, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error(`Error updating school ${schoolId}:`, error);
            throw error;
        }
    }

    // Update school status
    async updateSchoolStatus(schoolId, status) {
        try {
            // Based on the backend code, we need to send a SchoolStatus object
            const response = await this.api.patch(`/superadmin/schools/${schoolId}/status`, { status }, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error(`Error updating school ${schoolId} status:`, error);
            throw error;
        }
    }

    // Delete a school
    async deleteSchool(schoolId) {
        try {
            const response = await this.api.delete(`/superadmin/schools/${schoolId}`, {
                headers: this.getAuthHeaders()
            });
            return response.status === 204; // No content response for successful delete
        } catch (error) {
            console.error(`Error deleting school ${schoolId}:`, error);
            throw error;
        }
    }

    // Error handler helper
    handleError(error) {
        if (error.response?.status === 401) {
            this.logout();
            return 'Session expired. Please login again.';
        }

        // Handle validation errors (422 responses)
        if (error.response?.status === 422) {
            const validationErrors = error.response.data.detail;

            // If it's an array of validation errors, format them nicely
            if (Array.isArray(validationErrors)) {
                return validationErrors.map(err => err.msg || 'Validation error').join('. ');
            }

            // If it's a single error object or string
            if (typeof validationErrors === 'object') {
                return validationErrors.msg || 'Validation error';
            }

            return validationErrors || 'Validation error occurred';
        }

        return error.response?.data?.detail ||
            error.message ||
            'An unexpected error occurred';
    }

    // ──────────────────────────────────────────────────────────────────────────────
    // School Group Management
    // ──────────────────────────────────────────────────────────────────────────────

    // Create a school group with branches
    async createSchoolGroup(groupData) {
        try {
            console.log('Creating school group:', groupData.group_name);
            const response = await this.api.post('/superadmin/school-groups', groupData, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Error creating school group:', error);
            throw error;
        }
    }

    // Get all school groups
    async getSchoolGroups(params = {}) {
        try {
            const response = await this.api.get('/superadmin/school-groups', {
                headers: this.getAuthHeaders(),
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching school groups:', error);
            throw error;
        }
    }

    // Get branches of a school group
    async getGroupBranches(groupId) {
        try {
            const response = await this.api.get(`/superadmin/school-groups/${groupId}/branches`, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error(`Error fetching branches for group ${groupId}:`, error);
            throw error;
        }
    }

    // Add a branch to a school group
    async addBranchToGroup(groupId, branchData) {
        try {
            const response = await this.api.post(`/superadmin/school-groups/${groupId}/branches`, branchData, {
                headers: this.getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            console.error('Error adding branch to group:', error);
            throw error;
        }
    }

    // Remove a branch from a school group
    async removeBranchFromGroup(groupId, branchId, convertToSingle = false) {
        try {
            const response = await this.api.delete(
                `/superadmin/school-groups/${groupId}/branches/${branchId}`,
                {
                    headers: this.getAuthHeaders(),
                    params: { convert_to_single: convertToSingle }
                }
            );
            return response.status === 204;
        } catch (error) {
            console.error('Error removing branch from group:', error);
            throw error;
        }
    }

    // ──────────────────────────────────────────────────────────────────────────────
    // School Group Assignment
    // ──────────────────────────────────────────────────────────────────────────────

    // Assign an existing single school to an existing group as a branch
    async assignSchoolToGroup(schoolId, groupId) {
        try {
            const response = await this.api.post(
                `/superadmin/schools/${schoolId}/assign-to-group`,
                { group_id: groupId },
                { headers: this.getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error(`Error assigning school ${schoolId} to group ${groupId}:`, error);
            throw error;
        }
    }

    // Create a new school group from a set of existing single schools
    async createGroupFromExisting(groupData) {
        try {
            console.log('Creating group from existing schools:', groupData.group_name);
            const response = await this.api.post(
                '/superadmin/school-groups/from-existing',
                groupData,
                { headers: this.getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating group from existing schools:', error);
            throw error;
        }
    }
}

// Export a singleton instance
const superAdminAPI = new SuperAdminAPI();
export default superAdminAPI;